import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';
import PageIcon from '@/components/PageIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AreaDetailPage() {
  const { slug } = useParams();
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const heroImage = useHeroImage('home'); // Use home hero as backdrop

  useEffect(() => {
    loadArea();
  }, [slug]);

  const loadArea = async () => {
    try {
      const response = await axios.get(`${API}/areas/${slug}`);
      setArea(response.data);
    } catch (error) {
      console.error('Failed to load area:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!area) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl retro-title text-[#F4E4C1] mb-4">Area Not Found</h1>
            <a href="/" className="retro-button inline-flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </a>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="min-h-screen">
        {/* Hero Section */}
        <div className="relative h-[400px] overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${heroImage})`,
              filter: 'sepia(40%) saturate(120%) hue-rotate(-5deg) brightness(0.7)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#2C1810]/70 via-[#4D9E7C]/20 to-[#2C1810]/90"></div>
          
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
            <PageIcon pageName="home" size="large" />
            <h1 className="text-5xl md:text-7xl retro-title mb-4 text-center">{area.title}</h1>
            {area.subtitle && (
              <p className="text-2xl md:text-3xl text-[#F4E4C1] font-bold text-center drop-shadow-lg">
                {area.subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Back Button */}
          <div className="mb-8">
            <a 
              href="/" 
              className="inline-flex items-center gap-2 bg-[#2C1810]/90 text-[#F4E4C1] font-bold px-4 py-2 rounded-lg hover:bg-[#2C1810] transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg">Back to Home</span>
            </a>
          </div>

          {/* Content Section with Images */}
          <div className="space-y-12">
            {/* Description Section */}
            {area.description && (
              <div className="admin-card">
                <div className="prose prose-lg max-w-none">
                  <div className="text-[#F4E4C1] leading-relaxed whitespace-pre-wrap">
                    {area.description}
                  </div>
                </div>
              </div>
            )}

            {/* Two Column Layout with Images */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Image 1 */}
              {area.image_1_url && (
                <div className="cassette-card p-4 overflow-hidden">
                  <img 
                    src={area.image_1_url.startsWith('http') ? area.image_1_url : `${BACKEND_URL}${area.image_1_url}`}
                    alt={`${area.title} - Image 1`}
                    className="w-full h-64 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Image 2 */}
              {area.image_2_url && (
                <div className="cassette-card p-4 overflow-hidden">
                  <img 
                    src={area.image_2_url.startsWith('http') ? area.image_2_url : `${BACKEND_URL}${area.image_2_url}`}
                    alt={`${area.title} - Image 2`}
                    className="w-full h-64 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Additional Details Section */}
            {area.additional_details && (
              <div className="admin-card">
                <h2 className="text-3xl retro-title text-[#F4E4C1] mb-6">Additional Information</h2>
                <div className="prose prose-lg max-w-none">
                  <div className="text-[#F4E4C1] leading-relaxed whitespace-pre-wrap">
                    {area.additional_details}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <div className="cassette-card p-8">
              <h3 className="text-2xl retro-title text-[#3D2817] mb-4">
                Ready to Learn More?
              </h3>
              <p className="text-[#6B4423] mb-6">
                Contact us to schedule a consultation or learn more about our services.
              </p>
              <a href="/about" className="retro-button inline-block">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
